import { useEffect, useState } from 'react'
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import FormGroup from '@/core/components/FormGroup'
import { cn } from '@/lib/utils'
import { MultiSelect, MultiSelectOption } from '@/components/multi-select'
import { TagDto, TagUpsertDto } from '@/common/models/gamification'
import { createTag, getTags } from '@/core/services/gamification'

import { CreateTagModal } from '../CreateTagModal'
import { translationText } from '../shared/labels'
import { TagChip } from '../shared/TagChip'

interface TagsFieldProps {
  editionId: string
  /** Ordered tags currently selected. Controlled. */
  value: TagDto[]
  onChange: (next: TagDto[]) => void
}

const tagOption = (tag: TagDto): MultiSelectOption => ({
  label: translationText(tag.translations, 'Untitled tag'),
  value: tag.id,
})

const SortableTagChip = ({
  tag,
  onRemove,
}: {
  tag: TagDto
  onRemove: () => void
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tag.id })
  return (
    <TagChip
      ref={setNodeRef}
      tag={tag}
      onRemove={onRemove}
      className={cn(
        'cursor-grab select-none active:cursor-grabbing',
        isDragging && 'relative z-10 opacity-60',
      )}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      {...attributes}
      {...listeners}
    />
  )
}

/**
 * Tag picker for the quest/quest-group editors: a `MultiSelect` over the edition's existing tags,
 * a "+ New tag" affordance that creates one immediately (draft editors have no id to attach to
 * yet, but tags themselves are edition-level entities so they can be created right away), and the
 * currently-selected tags rendered as reorderable removable chips. Only mutates the caller's draft
 * state — attach/detach/reorder against a quest/group happens later, in `saveStep`, once the
 * quest/group itself has an id.
 */
export const TagsField = ({ editionId, value, onChange }: TagsFieldProps) => {
  const [allTags, setAllTags] = useState<TagDto[]>([])
  const [loading, setLoading] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [createError, setCreateError] = useState<string | undefined>()

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      if (!editionId) return
      setLoading(true)
      try {
        const { data } = await getTags(editionId)
        if (!cancelled) setAllTags(data ?? [])
      } catch {
        if (!cancelled) setAllTags([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [editionId])

  const handleMultiSelectChange = (next: MultiSelectOption[]) => {
    const nextTags = next
      .map(
        (opt) =>
          allTags.find((t) => t.id === opt.value) ??
          value.find((t) => t.id === opt.value),
      )
      .filter((t): t is TagDto => !!t)
    onChange(nextTags)
  }

  const handleCreateTag = async (values: TagUpsertDto) => {
    try {
      const { data } = await createTag(editionId, values)
      setAllTags((prev) => [...prev, data])
      onChange([...value, data])
      setCreateError(undefined)
    } catch (e) {
      // Surface the failure and rethrow so CreateTagModal keeps itself open instead of closing
      // as though the tag were created.
      setCreateError('Could not create the tag. Please try again.')
      throw e
    }
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    const oldIndex = value.findIndex((t) => t.id === active.id)
    const newIndex = value.findIndex((t) => t.id === over.id)
    if (oldIndex < 0 || newIndex < 0) return
    onChange(arrayMove(value, oldIndex, newIndex))
  }

  return (
    <FormGroup label="Tags">
      <div className="space-y-3">
        <MultiSelect
          options={allTags.map(tagOption)}
          value={value.map(tagOption)}
          onChange={handleMultiSelectChange}
          placeholder="Select tags"
          disabled={loading}
        />

        <a
          className="text-accent-1 underline text-sm"
          href="#"
          onClick={(e) => {
            e.preventDefault()
            setCreateError(undefined)
            setCreateOpen(true)
          }}
        >
          ＋ New tag
        </a>

        {createError && <p className="text-sm text-red-500">{createError}</p>}

        {value.length > 0 && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={value.map((t) => t.id)}
              strategy={horizontalListSortingStrategy}
            >
              <div className="flex flex-wrap gap-2">
                {value.map((tag) => (
                  <SortableTagChip
                    key={tag.id}
                    tag={tag}
                    onRemove={() =>
                      onChange(value.filter((t) => t.id !== tag.id))
                    }
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        <CreateTagModal
          open={createOpen}
          onOpenChange={setCreateOpen}
          tag={null}
          onSubmit={handleCreateTag}
        />
      </div>
    </FormGroup>
  )
}
